import Script from 'next/script';
import Head from 'next/head';
import { useEffect, useState } from 'react';

interface GTMScriptsProps {
  gtmId?: string;
  ga4Id?: string;
  loadOnInteraction?: boolean;
  debug?: boolean;
}

const GTMScripts = ({
  gtmId = 'GTM-NKSMZ26F',
  ga4Id = 'G-YLS8T7FSRC',
  loadOnInteraction = false,
  debug = false,
}: GTMScriptsProps) => {
  const [shouldLoad, setShouldLoad] = useState(!loadOnInteraction);

  useEffect(() => {
    if (loadOnInteraction && !shouldLoad) {
      // Load GTM on first user interaction (scroll, click, touch)
      const loadOnFirstInteraction = () => {
        setShouldLoad(true);
        // Remove event listeners once loaded
        document.removeEventListener('scroll', loadOnFirstInteraction);
        document.removeEventListener('click', loadOnFirstInteraction);
        document.removeEventListener('touchstart', loadOnFirstInteraction);
      };

      document.addEventListener('scroll', loadOnFirstInteraction, { once: true, passive: true });
      document.addEventListener('click', loadOnFirstInteraction, { once: true });
      document.addEventListener('touchstart', loadOnFirstInteraction, {
        once: true,
        passive: true,
      });

      // Fallback: load after 3 seconds if no interaction
      const fallbackTimer = setTimeout(() => {
        setShouldLoad(true);
      }, 3000);

      return () => {
        clearTimeout(fallbackTimer);
        document.removeEventListener('scroll', loadOnFirstInteraction);
        document.removeEventListener('click', loadOnFirstInteraction);
        document.removeEventListener('touchstart', loadOnFirstInteraction);
      };
    }
  }, [loadOnInteraction, shouldLoad]);

  if (!shouldLoad) {
    return (
      <Head>
        {/* Preconnect even before loading for faster connection when needed */}
        <link rel="dns-prefetch" href="//www.googletagmanager.com" />
        <link rel="dns-prefetch" href="//www.google-analytics.com" />
      </Head>
    );
  }

  return (
    <>
      {/* Performance Optimization - Resource Hints */}
      <Head>
        <link rel="dns-prefetch" href="//www.googletagmanager.com" />
        <link rel="dns-prefetch" href="//www.google-analytics.com" />
        <link rel="preconnect" href="https://www.googletagmanager.com" crossOrigin="" />
        <link rel="preconnect" href="https://www.google-analytics.com" crossOrigin="" />

        {/* Preload GTM script for faster loading */}
        <link
          rel="preload"
          href={`https://www.googletagmanager.com/gtm.js?id=${gtmId}`}
          as="script"
        />
        <link
          rel="preload"
          href={`https://www.googletagmanager.com/gtag/js?id=${ga4Id}`}
          as="script"
        />
      </Head>

      {/* Google Tag Manager - Optimized Implementation */}
      <Script
        id={`gtm-${gtmId}`}
        strategy="afterInteractive"
        onLoad={() => debug && console.log('GTM loaded successfully')}
        onError={() => debug && console.error('GTM failed to load')}
        dangerouslySetInnerHTML={{
          __html: `
            (function(w,d,s,l,i){
              w[l]=w[l]||[];
              w[l].push({'gtm.start': new Date().getTime(), event:'gtm.js'});
              var f=d.getElementsByTagName(s)[0],
                  j=d.createElement(s),
                  dl=l!='dataLayer'?'&l='+l:'';
              j.async=true;
              j.defer=true;
              j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl+'&cx=c&gtm=4e58m1';
              j.onload=function(){${debug ? "console.log('GTM script loaded');" : ''}};
              j.onerror=function(){${debug ? "console.error('GTM script failed');" : ''}};
              f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${gtmId}');
          `,
        }}
      />

      {/* Google Analytics 4 - Separate Optimized Loading */}
      <Script
        id="ga4-gtag"
        src={`https://www.googletagmanager.com/gtag/js?id=${ga4Id}`}
        strategy="afterInteractive"
        async
        defer
        onLoad={() => debug && console.log('GA4 script loaded')}
        onError={() => debug && console.error('GA4 script failed')}
      />

      <Script
        id="ga4-config"
        strategy="afterInteractive"
        onLoad={() => debug && console.log('GA4 config loaded')}
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${ga4Id}', {
              page_title: document.title,
              page_location: window.location.href,
              send_page_view: true,
              // Performance optimizations
              transport_type: 'beacon',
              anonymize_ip: true,
              allow_google_signals: false,
              allow_ad_personalization_signals: false
            });
            
            // Custom event for marketing page views
            if (window.location.pathname.includes('/marketing/')) {
              gtag('event', 'marketing_page_view', {
                page_path: window.location.pathname,
                campaign_code: window.location.pathname.split('/marketing/')[1]?.split('/')[0]
              });
            }
            
            ${debug ? "console.log('GA4 configuration complete');" : ''}
          `,
        }}
      />

      {/* NoScript Fallback for GTM */}
      <noscript>
        <iframe
          src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
          height="0"
          width="0"
          style={{ display: 'none', visibility: 'hidden' }}
        />
      </noscript>
    </>
  );
};
export default GTMScripts;
